import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useListStore } from '../../store/listStore';

interface NewListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NewListModal({ isOpen, onClose }: NewListModalProps) {
  const [name, setName] = useState('');
  const isSaving = useListStore((s) => s.isSaving);
  const saveError = useListStore((s) => s.saveError);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleClose = () => {
    setName('');
    onClose();
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed || isSaving) return;
    try {
      const list = await useListStore.getState().createList({ name: trimmed });
      setName('');
      onClose();
      navigate(`/lists/${list.id}`);
    } catch {
      // saveError already set by the store
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New list</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">List name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              placeholder="e.g., Top 30 Targets — 2026"
              className="input mt-1"
              autoFocus
            />
          </label>
          {saveError && <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-800">
          <button type="button" onClick={handleClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={!name.trim() || isSaving}
            className="btn-primary disabled:opacity-50"
          >
            {isSaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Create list
          </button>
        </div>
      </div>
    </div>
  );
}
